<script setup lang="ts">
import type { SerializedEditorState } from "lexical";
import { computed, ref } from "vue";
import { useRouter } from "vue-router";

import Button from "~/components/dumb/Button.vue";
import SkillCard from "~/components/dumb/SkillCard.vue";
import { ChevronRightIcon, ChevronUpIcon, SettingsIcon } from "~/icons";
import { makeLinkToSettingsRef } from "~/router/common";
import { ButtonStyle, IconSize, UserRole } from "~/shared/enums";
import type { Skill } from "~/shared/types";
import { useDataStore, useUserStore } from "~/stores";

const emit = defineEmits<{
  send: [message: SerializedEditorState];
}>();

const router = useRouter();
const dataStore = useDataStore();
const userStore = useUserStore();

const sendMessage = (skill: Skill) => {
  emit("send", skill.prompt);
};

const showingAllSkills = ref(false);
const skills = computed(() => dataStore.skillList());
const topThreeSkills = computed(() => skills.value.slice(0, 3));
</script>

<template>
  <div class="flex w-full select-none flex-col gap-2 overflow-auto">
    <div class="flex items-center justify-between">
      <span class="text-base font-semibold text-md">Skills</span>
      <Button
        v-if="userStore.isRoleGte(UserRole.MEMBER)"
        :btn-style="ButtonStyle.SECONDARY"
        :icon="SettingsIcon"
        :icon-size="IconSize.S"
        borderless
        a11y-label="Open skills settings"
        @click="() => router.push(makeLinkToSettingsRef('skills').value)" />
    </div>
    <div class="flex flex-col gap-2 overflow-y-auto pr-1">
      <div
        v-for="skill in showingAllSkills ? skills : topThreeSkills"
        :key="skill.duid"
        class="flex w-full cursor-pointer items-center justify-between gap-3 rounded-md border py-2 pl-4 pr-2 shadow-sm border-lt hover:bg-lt"
        @click="sendMessage(skill)"
        @keydown.enter="sendMessage(skill)">
        <SkillCard :skill="skill" truncate />
        <ChevronRightIcon class="text-lt icon-md" />
      </div>
    </div>
    <div v-if="skills.length > 3" class="mb-2 flex justify-start">
      <Button
        :btn-style="ButtonStyle.SECONDARY"
        :text="showingAllSkills ? `Show less` : `Show more`"
        text-style="text-xs"
        :icon="ChevronUpIcon"
        :icon-size="IconSize.S"
        :icon-args="{ class: ['transition-transform', !showingAllSkills && 'rotate-180'] }"
        borderless
        class="pl-1.5"
        @click="() => (showingAllSkills = !showingAllSkills)" />
    </div>
  </div>
</template>
